import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaStar, FaStarHalfAlt, FaRegStar, FaChevronDown, FaChevronUp, FaFilm, FaSearch, FaUser, FaHome, FaBars, FaTimes, FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import { films, getTierColor, getTypeColor, renderStars } from "../../data/HobbiesData/MoviesData/MovieData";
import { getFilterOptions, getTypeFilterOptions } from "../../data/HobbiesData/MoviesData/FillteredOptions";

const FilmReviewPage = () => {
  const navigate = useNavigate();
  const [showFullDescription, setShowFullDescription] = useState({});
  const [showFullReview, setShowFullReview] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeTypeFilter, setActiveTypeFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);

  const formatDescription = (text) => {
    if (!text) return "";
    return text.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split("\n").length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleDescription = (filmId) => {
    setShowFullDescription((prev) => ({
      ...prev,
      [filmId]: !prev[filmId],
    }));
  };

  const toggleReview = (filmId) => {
    setShowFullReview((prev) => ({
      ...prev,
      [filmId]: !prev[filmId],
    }));
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const toggleTypeFilter = () => {
    setIsTypeFilterOpen(!isTypeFilterOpen);
  };

  const handleFilter = (category) => {
    setActiveFilter(category);
    setIsFilterOpen(false);
  };

  const handleTypeFilter = (type) => {
    setActiveTypeFilter(type);
    setIsTypeFilterOpen(false);
  };

  const filteredFilms = films.filter((film) => {
    const matchesSearch = film.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         film.director.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         film.genre.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = activeFilter === "all" || film.category === activeFilter;
    const matchesTypeFilter = activeTypeFilter === "all" || film.type === activeTypeFilter;

    return matchesSearch && matchesFilter && matchesTypeFilter;
  });

  const filterOptions = getFilterOptions();
  const typeFilterOptions = getTypeFilterOptions();

  // Fungsi untuk mendapatkan deskripsi yang dipotong dengan format yang benar
  const getTruncatedText = (text, maxLength = 150) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;

    // Potong teks pada spasi terdekat setelah maxLength
    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(" ");

    return lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) + "..." : truncated + "...";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-stone-500 to-gray-800 text-white pixel-border-bottom relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-0 left-0 w-full h-full bg-repeat"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: "30px",
            }}
          ></div>
        </div>
        
        <div className="container mx-auto px-4 py-4 relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <motion.div className="mr-3" whileHover={{ rotate: 360 }} transition={{ type: "spring", stiffness: 300 }}>
                <FaFilm className="text-2xl" />
              </motion.div>
              <h1 className="text-xl md:text-3xl font-bold pixel-font">REVIEW FILM FAVORIT</h1>
            </div>

            <div className="hidden md:flex space-x-3">
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate("/")} className="flex items-center pixel-button bg-stone-700 hover:bg-stone-800 px-3 py-2 text-sm" title="Kembali ke Profil">
                <FaHome className="mr-1" />
                <span>Beranda</span>
              </motion.button>
            </div>

            <div className="md:hidden">
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleMobileMenu} className="pixel-button bg-stone-700 hover:bg-stone-800 p-2" aria-label="Toggle Menu">
                {isMobileMenuOpen ? <FaTimes className="text-lg" /> : <FaBars className="text-lg" />}
              </motion.button>
            </div>
          </div>

          <div className="flex mb-2">
            <div className={`flex items-center pixel-border bg-white px-3 py-2 transition-all duration-300 ${isSearchFocused ? "ring-2 ring-stone-400" : ""} w-full md:w-96`}>
              <FaSearch className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Cari judul, sutradara, atau genre..."
                className="bg-transparent outline-none text-gray-800 placeholder-gray-400 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
            </div>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div className="md:hidden bg-stone-700 pixel-border mt-4" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
                <div className="p-3 space-y-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleNavigation("/")}
                    className="w-full flex items-center justify-center pixel-button bg-stone-600 hover:bg-stone-800 px-3 py-2 text-sm"
                  >
                    <FaHome className="mr-2" />
                    <span>Kembali ke Beranda</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Filter Section - Added Type Filter */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          {/* Mobile Type Filter */}
          <div className="md:hidden relative w-full">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleTypeFilter} className="flex items-center justify-center pixel-button bg-red-600 hover:bg-red-700 px-4 py-2 w-full">
              <FaFilter className="mr-2" />
              <span>{typeFilterOptions.find((opt) => opt.value === activeTypeFilter)?.label}</span>
              {isTypeFilterOpen ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
            </motion.button>

            <AnimatePresence>
              {isTypeFilterOpen && (
                <motion.div className="absolute top-full left-0 right-0 mt-2 bg-white pixel-border z-50" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  {typeFilterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleTypeFilter(option.value)}
                      className={`flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors ${activeTypeFilter === option.value ? "bg-red-50 text-red-700 font-semibold" : "text-gray-800"}`}
                    >
                      <span>{option.label}</span>
                      <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">{option.count}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Tier Filter */}
          <div className="md:hidden relative w-full">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleFilter} className="flex items-center justify-center pixel-button bg-blue-600 hover:bg-blue-700 px-4 py-2 w-full">
              <FaFilter className="mr-2" />
              <span>{filterOptions.find((opt) => opt.value === activeFilter)?.label}</span>
              {isFilterOpen ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
            </motion.button>

            <AnimatePresence>
              {isFilterOpen && (
                <motion.div className="absolute top-full left-0 right-0 mt-2 bg-white pixel-border z-50" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleFilter(option.value)}
                      className={`flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors ${activeFilter === option.value ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-800"}`}
                    >
                      <span>{option.label}</span>
                      <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">{option.count}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Desktop Filters */}
        <div className="hidden md:flex flex-col gap-4 mb-6">
          {/* Type Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {typeFilterOptions.map((option) => {
              const typeColor = getTypeColor(option.value);
              return (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTypeFilter(option.value)}
                  className={`flex items-center pixel-button px-4 py-2 transition-all ${activeTypeFilter === option.value ? `${typeColor.bg} ${typeColor.text} ${typeColor.border} border-2` : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                >
                  {option.label}
                  <span className={`ml-2 text-xs px-2 py-1 rounded-full ${activeTypeFilter === option.value ? "bg-white text-gray-800" : "bg-gray-300 text-gray-700"}`}>{option.count}</span>
                </motion.button>
              );
            })}
          </div>

          {/* Tier Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {filterOptions.map((option) => {
              const tierColor = getTierColor(option.value);
              return (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveFilter(option.value)}
                  className={`flex items-center pixel-button px-4 py-2 transition-all ${activeFilter === option.value ? `${tierColor.bg} ${tierColor.text} ${tierColor.border} border-2` : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                >
                  {option.label}
                  <span className={`ml-2 text-xs px-2 py-1 rounded-full ${activeFilter === option.value ? "bg-white text-gray-800" : "bg-gray-300 text-gray-700"}`}>{option.count}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="text-center mb-8">
          <p className="text-gray-600 max-w-2xl mx-auto">Berikut adalah koleksi film-film yang telah saya tonton dan reviewnya.</p>
        </motion.div>

        {/* Filter Results Info */}
        {(searchQuery || activeFilter !== "all" || activeTypeFilter !== "all") && (
          <motion.div className="mb-6 p-3 bg-blue-50 pixel-border text-blue-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex flex-wrap items-center gap-2">
              <span>
                Menampilkan <span className="font-bold">{filteredFilms.length}</span> hasil
              </span>
              {searchQuery && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">Pencarian: "{searchQuery}"</span>}
              {activeTypeFilter !== "all" && (
                <span className={`${getTypeColor(activeTypeFilter).bg} ${getTypeColor(activeTypeFilter).text} px-2 py-1 rounded text-sm`}>
                  Jenis: {typeFilterOptions.find((opt) => opt.value === activeTypeFilter)?.label}
                </span>
              )}
              {activeFilter !== "all" && (
                <span className={`${getTierColor(activeFilter).bg} ${getTierColor(activeFilter).text} px-2 py-1 rounded text-sm`}>
                  Tier: {filterOptions.find((opt) => opt.value === activeFilter)?.label}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="text-blue-600 hover:text-blue-800 font-medium pixel-button bg-blue-100 px-3 py-1 text-sm">
                  Hapus Pencarian
                </button>
              )}
              {(activeFilter !== "all" || activeTypeFilter !== "all" || searchQuery) && (
                <button
                  onClick={() => {
                    setActiveFilter("all");
                    setActiveTypeFilter("all");
                    setSearchQuery("");
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium pixel-button bg-blue-100 px-3 py-1 text-sm"
                >
                  Reset Semua
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* Film List */}
        <div className="space-y-8">
          <AnimatePresence>
            {filteredFilms.map((film, index) => {
              const truncatedDescription = getTruncatedText(film.description, 150);
              const truncatedReview = getTruncatedText(film.review, 200);
              const isDescriptionTruncated = film.description.length > 150;
              const isReviewTruncated = film.review.length > 200;
              const tierColor = getTierColor(film.category);
              const typeColor = getTypeColor(film.type);

              return (
                <motion.div key={film.id} className="bg-white pixel-border p-4 md:p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} exit={{ opacity: 0, y: -20 }}>
                  <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="w-32 h-48 md:w-40 md:h-60 pixel-border overflow-hidden mx-auto">
                        <img src={film.image} alt={film.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      </div>
                      <div className={`mt-2 px-3 py-1 text-xs font-semibold rounded-full border-2 ${tierColor.bg} ${tierColor.text} ${tierColor.border}`}>
                        Tier {film.category}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
                        <h3 className="text-xl md:text-2xl font-bold text-gray-800 pixel-font">{film.title}</h3>
                        <div className="flex items-center mt-1 md:mt-0">
                          <span className="text-gray-600 mr-2 text-sm">Rating Saya:</span>
                          <div className="flex items-center">
                            <div className="flex text-yellow-500">
                              {renderStars(film.personalRating)}
                            </div>
                            <span className="ml-2 text-yellow-600 font-semibold">{film.personalRating}/5</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-3 text-sm md:text-base">
                        disutradarai oleh <span className="font-semibold">{film.director}</span>
                      </p>

                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          <div className="w-3 h-3 bg-blue-600 mr-2"></div>
                          Sinopsis Film
                        </h4>
                        <div className="text-gray-700 leading-relaxed text-sm md:text-base whitespace-pre-line">
                          {showFullDescription[film.id] ? formatDescription(film.description) : formatDescription(truncatedDescription)}
                          {isDescriptionTruncated && (
                            <button onClick={() => toggleDescription(film.id)} className="text-blue-600 hover:text-blue-800 font-medium ml-2 flex items-center mt-1">
                              {showFullDescription[film.id] ? (
                                <>
                                  <span>Sembunyikan</span>
                                  <FaChevronUp className="ml-1" />
                                </>
                              ) : (
                                <>
                                  <span>Selengkapnya</span>
                                  <FaChevronDown className="ml-1" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          <div className="w-3 h-3 bg-green-600 mr-2"></div>
                          Review Saya
                        </h4>
                        <div className="text-gray-700 leading-relaxed text-sm md:text-base whitespace-pre-line">
                          {showFullReview[film.id] ? formatDescription(film.review) : formatDescription(truncatedReview)}
                          {isReviewTruncated && (
                            <button onClick={() => toggleReview(film.id)} className="text-blue-600 hover:text-blue-800 font-medium ml-2 flex items-center mt-1">
                              {showFullReview[film.id] ? (
                                <>
                                  <span>Sembunyikan</span>
                                  <FaChevronUp className="ml-1" />
                                </>
                              ) : (
                                <>
                                  <span>Selengkapnya</span>
                                  <FaChevronDown className="ml-1" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          <div className="w-3 h-3 bg-purple-600 mr-2"></div>
                          Detail Film
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center">
                            <span className="font-medium text-gray-600 mr-2">Genre:</span>
                            <span className="text-gray-800">{film.genre}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium text-gray-600 mr-2">Durasi:</span>
                            <span className="text-gray-800">{film.duration}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium text-gray-600 mr-2">Rilis:</span>
                            <span className="text-gray-800">{film.releaseYear}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="font-medium text-gray-600 mr-2">Rating IMDb:</span>
                            <span className="text-gray-800">{film.rating}/10</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {filteredFilms.length === 0 && (
          <motion.div className="text-center py-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <p className="text-gray-600 mb-4">Tidak ditemukan film yang sesuai dengan kriteria yang dipilih.</p>
            <button
              onClick={() => {
                setActiveFilter("all");
                setActiveTypeFilter("all");
                setSearchQuery("");
              }}
              className="pixel-button bg-blue-600 text-white px-6 py-2 hover:bg-blue-700"
            >
              Tampilkan Semua Film
            </button>
          </motion.div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default FilmReviewPage;